export type NavigationPage = { pageNumber: number; start: number; stringVersion: number };
export type PageTarget = number | { start: number; stringVersion: number };

/** Owns scroll commands separately from the current-page URL. */
export class PageNavigation {
  private pages: NavigationPage[] = [];
  private pending: PageTarget | undefined;
  private applyingScroll = false;
  private lastScrollTop: number;
  private generation = 0;
  private frame = 0;
  private current: number | undefined;
  private observer: MutationObserver;
  private resizeObserver: ResizeObserver;

  constructor(private root: HTMLElement, private publish: (page: number) => void) {
    this.lastScrollTop = root.scrollTop;
    root.addEventListener("scroll", this.onScroll, { passive: true });
    root.addEventListener("wheel", this.cancelPendingNavigation, { passive: true });
    root.addEventListener("touchmove", this.cancelPendingNavigation, { passive: true });
    root.addEventListener("pointerdown", this.scrollbar);
    document.addEventListener("keydown", this.keyboard);
    this.observer = new MutationObserver(() => {
      if (this.pending !== undefined) this.flush();
    });
    this.observer.observe(root, { childList: true, subtree: true });
    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(root);
  }

  setPages(pages: NavigationPage[]) {
    this.pages = pages;
    if (this.current !== undefined && !pages.some(p => p.pageNumber === this.current)) {
      this.schedule();
    }
    this.flush();
  }

  requestPage = (number: number): boolean => {
    if (!Number.isInteger(number) || !this.pages.some(p => p.pageNumber === number)) return false;
    this.cancelTracking();
    this.generation++;
    this.pending = number;
    this.flush();
    return true;
  };

  /** Capture intent before an async edit; later user actions supersede it. */
  beginNavigation = () => {
    const generation = ++this.generation;
    this.pending = undefined;
    return (target: PageTarget) => {
      if (generation !== this.generation) return;
      this.cancelTracking();
      this.pending = target;
      this.flush();
    };
  };

  recordCurrentPage = (number: number) => {
    if (!this.pages.some(p => p.pageNumber === number)) return;
    this.cancelTracking();
    this.generation++;
    this.pending = undefined;
    this.lastScrollTop = this.root.scrollTop;
    this.schedule();
  };

  private commit(number: number) {
    const url = new URL(window.location.href);
    if (url.searchParams.get("displayedPageNumber") !== String(number)) {
      url.searchParams.set("displayedPageNumber", String(number));
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
    if (this.current !== number) {
      this.current = number;
      this.publish(number);
    }
  }

  private flush() {
    const target = this.pending;
    if (target === undefined) return;
    const page = this.pages.find(p => typeof target === "number"
      ? p.pageNumber === target
      : p.start === target.start && p.stringVersion === target.stringVersion);
    if (!page) return;
    const element = this.root.querySelector<HTMLElement>(`#page-${page.pageNumber}`);
    if (!element) return;
    // Consume before scrolling: layout/observer callbacks cannot replay the command.
    this.pending = undefined;
    this.cancelTracking();
    this.commit(page.pageNumber);
    const top = this.root.scrollTop + element.getBoundingClientRect().top
      - this.root.getBoundingClientRect().top - this.root.clientTop;
    this.applyingScroll = true;
    try {
      this.root.scrollTo({ top, behavior: "instant" });
    } finally {
      // Remember the actual destination, including browser clamping at the last page.
      // The resulting scroll event is redundant; any subsequent movement is tracked.
      this.lastScrollTop = this.root.scrollTop;
      this.applyingScroll = false;
    }
  }

  private cancelTracking() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private onScroll = () => {
    if (this.applyingScroll || this.root.scrollTop === this.lastScrollTop) return;
    this.lastScrollTop = this.root.scrollTop;
    this.cancelPendingNavigation();
    this.schedule();
  };

  private schedule = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      const bounds = this.root.getBoundingClientRect();
      const top = bounds.top + this.root.clientTop;
      const bottom = top + this.root.clientHeight;
      const left = bounds.left + this.root.clientLeft;
      const right = left + this.root.clientWidth;
      const first = Array.from(this.root.querySelectorAll<HTMLElement>(".pageContent"))
        .find(element => {
          const rect = element.getBoundingClientRect();
          return rect.height > 0 && rect.width > 0
            && rect.top >= top - 0.5 && rect.bottom <= bottom + 0.5
            && rect.left >= left - 0.5 && rect.right <= right + 0.5;
        });
      if (first) this.commit(Number(first.id.slice("page-".length)));
    });
  };

  private cancelPendingNavigation = () => {
    this.generation++;
    this.pending = undefined;
  };

  private scrollbar = (event: PointerEvent) => {
    const bounds = this.root.getBoundingClientRect();
    if (event.target === this.root && event.clientX >= bounds.left + this.root.clientWidth) {
      this.cancelPendingNavigation();
    }
  };

  private keyboard = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest("input, textarea, select, button, [contenteditable=true], [role=spinbutton], [role=slider]")) return;
    if (target !== document.body && target !== document.documentElement && !this.root.contains(target)) return;
    if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) {
      this.cancelPendingNavigation();
    }
  };

  destroy() {
    this.generation++;
    this.pending = undefined;
    this.cancelTracking();
    this.observer.disconnect();
    this.resizeObserver.disconnect();
    this.root.removeEventListener("scroll", this.onScroll);
    this.root.removeEventListener("wheel", this.cancelPendingNavigation);
    this.root.removeEventListener("touchmove", this.cancelPendingNavigation);
    this.root.removeEventListener("pointerdown", this.scrollbar);
    document.removeEventListener("keydown", this.keyboard);
  }
}
